import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Check } from 'lucide-react';

import useTenantState from '@/states/tenant.state';
import useJobDescriptionState from '@/states/job.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import type { IJobDescriptionPeran } from '@/types/job-description-peran.types';
import ReportPeranTable from '@/components/company/report/ReportPeranTable';
import { countUploadedByNode } from '@/components/company/report/report.utils';
import StepTipsPanel from '@/components/company/StepTipsPanel';

export default function Report() {
  const navigate = useNavigate();
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const { currentTenant } = useTenantState();
  const isReadOnly = useIsReadOnly();
  const [showPanel, setShowPanel] = useState(true);
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const {
    peranList,
    peranPaginated,
    peranLoading,
    getJobDescriptionPeran,
    jobDescriptionsFull,
    getJobDescriptionsFull,
  } = useJobDescriptionState();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const reportStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'report');
  const videoLink = reportStep?.video_link;
  const tips = reportStep?.tips;

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    order: 'asc',
    orderBy: 'org_level',
  });

  useEffect(() => {
    if (!currentTenant?.id) return;
    getJobDescriptionPeran(currentTenant.id, {
      page: pageConfig.page,
      page_size: pageConfig.limit,
      order: pageConfig.order,
      order_by: pageConfig.orderBy as 'name' | 'job_description_count' | 'org_level',
    });
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    getJobDescriptionPeran,
  ]);

  useEffect(() => {
    if (!currentTenant?.id) return;
    getJobDescriptionsFull(currentTenant.id);
  }, [currentTenant?.id, getJobDescriptionsFull]);

  const uploadedCounts = useMemo(() => countUploadedByNode(jobDescriptionsFull), [jobDescriptionsFull]);

  const handleDetail = (row: IJobDescriptionPeran) => {
    navigate(`/${tenant_id}/report/peran/${row.id}`);
  };

  return (
    <section className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Report</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Menyusun format laporan pekerjaan setiap karyawan yang terukur dan berstruktur.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={async () => {
                await refetchCompletionStatus();
                navigate(`/${tenant_id}/kpi`);
              }}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              Simpan & Lanjut
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <ReportPeranTable
              isLoading={peranLoading}
              data={{
                content: peranList,
                totalElements: peranPaginated?.totalElements ?? 0,
                totalPages: peranPaginated?.totalPages ?? 0,
              }}
              uploadedCounts={uploadedCounts}
              pageConfig={pageConfig}
              setPageConfig={setPageConfig}
              onDetail={handleDetail}
            />
          </div>
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
